<script setup>
import {ref, onBeforeUnmount, watch} from "vue";
import {createStream} from "@/services/stream.service";
import {createCSV, download} from "@/helpers/file";

const showModal = ref(false);
const question = ref("");
const loading = ref(false);
const stream = ref(null);
const rounds = ref([]);
const responseId = ref('');
const systemMessages = ref([]);
const loaderMessage = ref('');
const content = ref(null);
const debugVisible = ref(false);
const copiedIndex = ref(null);
const collapsedMessages = ref(new Set());

function downloadFile(file) {
    let columns = file.columns.reduce((acc, column) => {
        acc[column.column] = column.label;
        return acc;
    }, {});

    let data = file.data.map(item =>
        file.columns.reduce((row, column) => {
            row[column.column] = item[column.column];
            return row;
        }, {})
    );

    download(createCSV(data, columns), 'response.csv');
}

function sanitizeAnswer(answer) {
    let escaped = answer;

    if (loading.value) {
        // escape HTML tags during streaming
        escaped = escaped.replace(/<[^>]*>/g, '');
    }

    return escaped.replace(/\r\n|\n|\r/g, '<br>');
}

function clearStream() {
    if (stream.value) {
        stream.value.close();
        stream.value = null;
    }
}

function sendRequest() {
    if (!question.value.trim()) {
        return;
    }

    // close previous stream if still open to avoid multiple concurrent connections
    clearStream();

    const params = new URLSearchParams();

    params.set('question', btoa(unescape(encodeURIComponent(question.value))));

    if (responseId.value?.length) {
        params.set('response_id', btoa(unescape(encodeURIComponent(responseId.value))));
    }

    let round = {
        question: question.value,
        answer: '',
        output: null
    };

    stream.value = createStream('/chat-stream/get-ai-response?' + params.toString());

    if (!stream.value) {
        round.answer = 'Failed to open connection. Please try again.';
        loading.value = false;

        return;
    }

    rounds.value.push(round);
    question.value = '';
    loading.value = true;

    stream.value.onerror = (err) => {
        round.answer = 'Failed to open connection. Please try again.';
        console.error('SSE error', err);
        clearStream();

        // stop loading if connection fails
        loading.value = false;
    };

    stream.value.onmessage = function(event) {
        loaderMessage.value = '';
        round.answer += event.data;
    };

    stream.value.addEventListener('system', function(event) {
        systemMessages.value.push(event.data);
    });

    stream.value.addEventListener('loader', function(event) {
        loaderMessage.value = event.data;
    });

    stream.value.addEventListener('done', function(event) {
        const data = JSON.parse(event.data);
        loaderMessage.value = '';

        round.output = data.output;
        round.answer = data.answer;
        responseId.value = data.response_id;

        loading.value = false;

        stream.value.close();
        stream.value = null;
    });

    requestAnimationFrame(() => {
        content.value.scrollTop = content.value.scrollHeight;
    });
}

/* Debug popup functions */
function copyMessage(message, index) {
    navigator.clipboard.writeText(message).then(() => {
        copiedIndex.value = index;
        setTimeout(() => { copiedIndex.value = null; }, 2000);
    });
}

function toggleMessage(index) {
    const set = new Set(collapsedMessages.value);
    if (set.has(index)) {
        set.delete(index);
    } else {
        set.add(index);
    }
    collapsedMessages.value = set;
}
/* End Debug popup functions */


// ensure we always close stream when modal is hidden or component unmounts
watch(showModal, (value) => {
    if (!value && stream.value) {
        try { stream.value.close(); } catch (e) {
            /* ignore */
        }
        stream.value = null;
    }
});

onBeforeUnmount(() => {
    if (stream.value) {
        try { stream.value.close(); } catch (e) {
            /* ignore */
        }
        stream.value = null;
    }
});

function open(predefinedQuestion) {
    rounds.value = [];
    responseId.value = '';
    loading.value = false;
    question.value = predefinedQuestion;
    systemMessages.value = [];

    debugVisible.value = false;
    showModal.value = true;
    sendRequest();
}

defineExpose({
    open
});
</script>

<template>
    <b-modal v-model="showModal" hide-footer hide-header modal-class="modal-ai-chat">
        <!-- <a href="#" class="close">Back</a> -->
        <section class="section-ai-chat" ref="content">
            <button @click="showModal = false" type="button" aria-label="Close" class="close"></button>

            <div class="ai-chat__inner">
                <div class="ai-chat__content">
                    <!-- <h5 class="heading5 heading5--secondary">Find VCs, accelerators, universities, incubators and more.</h5> -->
                    
                    <div class="ai-chat">
                        <div
                            v-for="(round, index) in rounds"
                            :key="index"
                            class="ai-chat__inner"
                        >
                            <div class="ai-chat__user">
                                {{round.question}}
                            </div>

                            <div class="ai-chat__machine" :class="{'long-div': index === rounds.length - 1}">
                                <div v-if="loaderMessage.length && index === rounds.length -1">
                                    <div class="ai-loader">
                                        <span>{{loaderMessage}}</span>
                                        <span class="ai-loader__circle ml-2"></span>
                                        <span class="ai-loader__circle"></span>
                                        <span class="ai-loader__circle"></span>
                                    </div>
                                </div>

                                <p v-html="sanitizeAnswer(round.answer)"/>

                                <table class="ai-chat__table" v-if="round.output?.table">
                                    <thead>
                                    <tr>
                                        <th v-for="column in round.output.table.columns" :key="column.column">
                                            {{ column.label }}
                                        </th>
                                    </tr>
                                    </thead>

                                    <tbody>
                                        <tr v-for="(row, rowIndex) in round.output.table.data" :key="rowIndex">
                                            <td v-for="column in round.output.table.columns" :key="column.column">
                                                <a
                                                    v-if="round.output.table.company_data_fields && round.output.table.company_data_fields.name === column.column"
                                                    :href="`/startups/${row[round.output.table.company_data_fields.id]}`"
                                                    target="_blank"
                                                >
                                                    {{ row[round.output.table.company_data_fields.name] }}
                                                </a>

                                                <template v-else>
                                                    {{ row[column.column] }}
                                                </template>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <button
                                    v-if="round.output?.file"
                                    class="button-csv"
                                    @click="downloadFile(round.output.file)"
                                >
                                    Download CSV
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="ai-chat__footer">
                        <div v-if="!loading" class="card--ai__search">
                            <input
                                v-focus
                                v-model="question"
                                placeholder="Ask about startups or partnerships"
                                class="card--ai__search__input"
                                @keyup.enter="sendRequest"
                            >

                            <button class="card--ai__search__button" @click="sendRequest"></button>
                        </div>
                    </div>

                    <!-- Dev debug zone: invisible click trigger in bottom-left corner -->
                    <div class="debug-trigger" @click.stop="debugVisible = !debugVisible">
                        <transition name="debug-fade">
                            <div v-if="debugVisible" class="debug-panel" @click.stop>
                                <div class="debug-panel__header">
                                    <span class="debug-panel__title">🛠 Debug Panel</span>
                                    <a class="debug-close" @click.stop="debugVisible = false">✕</a>
                                </div>

                                <div class="debug-panel__section">
                                    <div class="debug-panel__section-header">
                                        <span>System Messages <span class="debug-badge">{{ systemMessages.length }}</span></span>
                                        <a class="debug-clear" @click.stop="systemMessages = []">Clear all</a>
                                    </div>
                                    <div class="debug-panel__body">
                                        <div v-if="systemMessages.length === 0" class="debug-empty">No messages yet</div>
                                        <div v-for="(message, index) in systemMessages" :key="index" class="debug-message">
                                            <div class="debug-message__header">
                                                <span class="debug-index" @click.stop="toggleMessage(index)">
                                                    <span class="debug-chevron">{{ collapsedMessages.has(index) ? '▶' : '▼' }}</span>
                                                    #{{ index + 1 }}
                                                    <span v-if="collapsedMessages.has(index)" class="debug-preview">{{ message.slice(0, 60).replace(/\s+/g, ' ') }}…</span>
                                                </span>
                                                <button class="debug-copy" @click.stop="copyMessage(message, index)" :class="{ copied: copiedIndex === index }">
                                                    {{ copiedIndex === index ? '✓ Copied' : 'Copy' }}
                                                </button>
                                            </div>
                                            <pre v-if="!collapsedMessages.has(index)" class="debug-message__text">{{ message }}</pre>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </transition>
                    </div>
                </div>
            </div>
        </section>
    </b-modal>
</template>

<style scoped>
    .long-div {
        min-height: 140px;
    }

    .section-ai-chat {
        overflow-y: auto;
        scroll-behavior: smooth;
        padding-bottom: 160px;
    }

    .ai-chat__footer {
        pointer-events: none;
    }

    .ai-chat__footer .card--ai__search {
        pointer-events: auto;
    }

    /* Invisible click trigger in bottom-left corner */
    .debug-trigger {
        position: fixed;
        bottom: 0;
        left: 0;
        width: 60px;
        height: 60px;
        z-index: 9999;
        cursor: default;
    }

    /* Debug panel floats above the trigger, outside chat flow */
    .debug-panel {
        position: absolute;
        bottom: 60px;
        left: 0;
        width: 460px;
        max-height: 75vh;
        background: #fff;
        border: 1px solid #d2dde1;
        border-radius: 8px;
        box-shadow: 0 8px 32px rgba(4, 54, 72, 0.18);
        overflow: hidden;
        display: flex;
        flex-direction: column;
        font-family: 'Menlo', 'Monaco', 'Consolas', monospace;
        font-size: 12px;
        color: #043648;
    }

    .debug-panel__header {
        padding: 10px 14px;
        background: #043648;
        border-bottom: 1px solid #032e3d;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .debug-panel__title {
        font-weight: 600;
        font-size: 13px;
        color: #fff;
        letter-spacing: 0.03em;
    }

    .debug-close {
        color: #99cee0;
        cursor: pointer;
        font-size: 13px;
        line-height: 1;
    }

    .debug-close:hover {
        color: #fff;
    }

    .debug-panel__section {
        display: flex;
        flex-direction: column;
        min-height: 0;
        border-bottom: 1px solid #d4eaf2;
    }

    .debug-panel__section:last-child {
        border-bottom: none;
    }

    .debug-panel__section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 7px 14px;
        background: #ebf7fb;
        font-weight: 600;
        color: #0083b2;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        border-bottom: 1px solid #d4eaf2;
    }

    .debug-badge {
        display: inline-block;
        background: #0083b2;
        color: #fff;
        border-radius: 8px;
        padding: 0 6px;
        font-size: 10px;
        font-weight: 700;
        margin-left: 5px;
    }

    .debug-clear {
        color: #ff6a6a;
        cursor: pointer;
        font-size: 10px;
        text-transform: none;
        letter-spacing: 0;
        font-weight: 400;
    }

    .debug-clear:hover {
        color: #a50000;
        text-decoration: underline;
    }

    .debug-panel__body {
        padding: 8px 14px;
        overflow-y: auto;
        max-height: 50vh;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .debug-empty {
        color: #95a8b0;
        font-style: italic;
        font-size: 11px;
        padding: 4px 0;
    }

    .debug-message {
        display: flex;
        flex-direction: column;
        gap: 4px;
        background: #f4f4f4;
        border: 1px solid #e1e1e1;
        border-radius: 5px;
    }

    .debug-message__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 4px 8px;
        background: #ebf7fb;
        border-bottom: 1px solid #d4eaf2;
        gap: 8px;
        min-width: 0;
    }

    .debug-index {
        color: #0083b2;
        font-weight: 700;
        font-size: 11px;
        cursor: pointer;
        user-select: none;
        display: flex;
        align-items: center;
        gap: 4px;
        flex: 1;
        min-width: 0;
    }

    .debug-index:hover {
        color: #043648;
    }

    .debug-chevron {
        font-size: 9px;
        flex-shrink: 0;
    }

    .debug-preview {
        color: #5e7e95;
        font-weight: 400;
        font-size: 10px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
        min-width: 0;
    }

    .debug-copy {
        background: #0083b2;
        color: #fff;
        border: none;
        border-radius: 4px;
        padding: 2px 8px;
        font-size: 10px;
        cursor: pointer;
        font-family: inherit;
        transition: background 0.15s;
    }

    .debug-copy:hover {
        background: #043648;
    }

    .debug-copy.copied {
        background: #1e7d3f;
    }

    .debug-message__text {
        margin: 0;
        padding: 6px 8px;
        white-space: pre-wrap;
        word-break: break-word;
        line-height: 1.5;
        color: #043648;
        font-size: 11px;
    }

    .debug-log {
        white-space: pre-wrap;
        word-break: break-word;
        color: #1e7d3f;
        margin: 0;
        line-height: 1.5;
    }

    /* Fade transition */
    .debug-fade-enter-active,
    .debug-fade-leave-active {
        transition: opacity 0.2s ease, transform 0.2s ease;
    }

    .debug-fade-enter-from,
    .debug-fade-leave-to {
        opacity: 0;
        transform: translateY(6px);
    }
</style>
